import { WebTracerProvider, BatchSpanProcessor } from '@opentelemetry/sdk-trace-web';
import { registerInstrumentations } from '@opentelemetry/instrumentation';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { Resource } from '@opentelemetry/resources';
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load';
import { SemanticResourceAttributes, SemanticAttributes } from '@opentelemetry/semantic-conventions';
export { SemanticAttributes, SemanticResourceAttributes } from '@opentelemetry/semantic-conventions';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { createContextKey, context, trace, SpanKind, SpanStatusCode } from '@opentelemetry/api';
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { getCookieValue, maskData } from './helpers.js';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

const getTimeStamp = () => {
    return new Date(Date.now()).toISOString();
};
function sendSpecificSpan(opts = {
    attributes: {},
    error: 'unknown-error',
    method: 'unknown-method',
    spanEvents: [],
    tracerName: 'unknown-span-name',
    urlReference: 'unknown-url-reference'
}) {
    const { attributes, error, method, spanEvents, tracerName, urlReference } = opts;
    const otelSessionId = getCookieValue('cb_ucid') || '';
    const key = createContextKey(tracerName);
    const ctx = context.active().setValue(key, {});
    context.with(ctx, () => __awaiter(this, void 0, void 0, function* () {
        const tracer = trace.getTracer(tracerName);
        const spanName = method ? `${method} ${tracerName}` : tracerName;
        const span = tracer.startSpan(spanName, {
            root: true,
            kind: SpanKind.SERVER,
            attributes: Object.assign(Object.assign({ 
                // the HTTP_METHOD and HTTP_URL are needed for the options to show up as a request within Kibana, otherwise they are placed in the UNKNOWN bucket
                [SemanticAttributes.HTTP_METHOD]: method, [SemanticAttributes.HTTP_URL]: urlReference }, (otelSessionId && { 'app.session_id': otelSessionId })), attributes)
        });
        trace.setSpan(context.active(), span);
        // Used to log error messages thrown as exception
        if (error) {
            span.recordException(new Error(error));
            span.setStatus({ code: SpanStatusCode.ERROR });
            span.end();
            return;
        }
        // should not exceed about 45k characters total between the attributes and the events
        if (spanEvents.length > 0) {
            spanEvents.forEach((ele) => span.addEvent(ele));
        }
        span.setStatus({ code: SpanStatusCode.OK });
        span.end();
    }));
}
const successStatusesWithResponse = [200, 201, 206];
function runWithSpan(parentSpan, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
fn, tracerName, sensitiveFields, duration, skipResponseLog, returnErrorBody) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    return __awaiter(this, void 0, void 0, function* () {
        const ctx = trace.setSpan(context.active(), parentSpan);
        try {
            const startTime = getTimeStamp();
            const response = yield context.with(ctx, fn);
            const endTime = getTimeStamp();
            // duration of the call in milliseconds
            const callTime = duration || new Date(endTime).getTime() - new Date(startTime).getTime();
            const status = (response === null || response === void 0 ? void 0 : response.status) ||
                (response === null || response === void 0 ? void 0 : response.httpStatus) ||
                ((_b = (_a = response === null || response === void 0 ? void 0 : response.data) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status) ||
                ((_d = (_c = response === null || response === void 0 ? void 0 : response.body) === null || _c === void 0 ? void 0 : _c.response) === null || _d === void 0 ? void 0 : _d.status) ||
                'unknownStatus'; // To return error status like 401, 404
            parentSpan.setAttribute('app.response_status', status);
            parentSpan.addEvent(`${tracerName} response status ${status}`);
            let formattedData;
            let error;
            if (successStatusesWithResponse.includes(status)) {
                // Edge case when data provider API or SA doesn't return any data or boolean for successful calls
                if (((_e = response === null || response === void 0 ? void 0 : response.data) === null || _e === void 0 ? void 0 : _e.data) === '' || ((_f = response === null || response === void 0 ? void 0 : response.body) === null || _f === void 0 ? void 0 : _f.data) === '') {
                    formattedData = 'OK'; // status text
                }
                else {
                    formattedData = JSON.stringify((response === null || response === void 0 ? void 0 : response.data) || (response === null || response === void 0 ? void 0 : response.body) || 'unknownData');
                }
            }
            else if (status === 204) {
                formattedData = 'No Content';
            }
            else {
                formattedData =
                    status === 404
                        ? 'Not Found'
                        : JSON.stringify(((_h = (_g = response === null || response === void 0 ? void 0 : response.data) === null || _g === void 0 ? void 0 : _g.response) === null || _h === void 0 ? void 0 : _h.data) ||
                            ((_k = (_j = response === null || response === void 0 ? void 0 : response.body) === null || _j === void 0 ? void 0 : _j.response) === null || _k === void 0 ? void 0 : _k.data) ||
                            'unknownData');
                error = JSON.stringify(((_l = response === null || response === void 0 ? void 0 : response.data) === null || _l === void 0 ? void 0 : _l.message) ||
                    ((_m = response === null || response === void 0 ? void 0 : response.body) === null || _m === void 0 ? void 0 : _m.message) ||
                    `Request failed with status: ${status}`);
                parentSpan.setAttribute('app.response_error', error);
                parentSpan.addEvent(`${tracerName} response error: ${error}`);
            }
            const skipLoggingResponse = skipResponseLog && status === 200;
            // the amount of data sent to attributes and events seems to be limited and if exceeded
            // the processing of the span does not go through.
            // attributes are automatically at 1024 characters by Kibana, so do that for size here
            if (!skipLoggingResponse && formattedData !== 'unknownData') {
                const maskedResponse = maskData(sensitiveFields, formattedData);
                parentSpan.setAttribute('app.response_data', maskedResponse.substring(0, 1024));
                // events may have some sort of limit, which seems to be around 25k or so characters
                parentSpan.addEvent(`${tracerName} response: ${maskedResponse}`);
            }
            parentSpan.setAttribute('app.response_duration', callTime);
            parentSpan.addEvent(`${tracerName} response duration ${callTime}ms`);
            if (![...successStatusesWithResponse, 204].includes(status)) {
                const errorResponse = ((_o = response === null || response === void 0 ? void 0 : response.data) === null || _o === void 0 ? void 0 : _o.response) ||
                    ((_p = response === null || response === void 0 ? void 0 : response.body) === null || _p === void 0 ? void 0 : _p.response) ||
                    (response === null || response === void 0 ? void 0 : response.body) ||
                    {};
                /* For javascript fetch calls */
                const statusText = (response === null || response === void 0 ? void 0 : response.statusText) || null;
                const ok = (response === null || response === void 0 ? void 0 : response.ok) || null;
                if (returnErrorBody) {
                    return {
                        body: errorResponse,
                        status,
                        statusText,
                        ok,
                        failure: true
                    };
                }
                return Object.assign(Object.assign({}, errorResponse), { status,
                    statusText,
                    ok, httpStatus: status, failure: true });
            }
            return response;
        }
        catch (error) {
            parentSpan.recordException(error);
            parentSpan.setStatus({ code: SpanStatusCode.ERROR });
            throw error;
        }
    });
}
function spanRequest(opts = {
    requestFn: null,
    tracerName: 'unknown-tracer',
    method: 'unknown-method',
    url: 'unknown-url',
    originalUrl: '',
    attributes: {},
    payload: null,
    sensitiveFields: [],
    duration: null,
    skipResponseLog: false,
    returnErrorBody: false
}) {
    return new Promise((resolve, reject) => {
        const { requestFn, tracerName, method, url, attributes, originalUrl, payload = null, sensitiveFields = [], duration = null, skipResponseLog = false, returnErrorBody = false } = opts;
        if (!requestFn) {
            return resolve(null);
        }
        const otelSessionId = getCookieValue('cb_ucid') || '';
        const key = createContextKey(tracerName);
        const ctx = context.active().setValue(key, {});
        context.with(ctx, () => __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const tracer = trace.getTracer(tracerName);
            const span = tracer.startSpan(`${method} ${tracerName}`, {
                root: true,
                kind: SpanKind.SERVER,
                attributes: Object.assign(Object.assign({ [SemanticAttributes.HTTP_METHOD]: method, [SemanticAttributes.HTTP_URL]: url }, (otelSessionId && { 'app.session_id': otelSessionId })), attributes)
            });
            trace.setSpan(context.active(), span);
            try {
                span.addEvent(`${tracerName} starting`);
                if (payload) {
                    const maskedRequest = maskData(sensitiveFields, payload);
                    span.addEvent(`${tracerName} request payload: ${maskedRequest}`);
                    span.setAttribute('app.http_request_body_content', maskedRequest);
                }
                const response = yield runWithSpan(span, requestFn, tracerName, sensitiveFields, duration, skipResponseLog, returnErrorBody);
                // Use the "originalUrl" passed in by the consumer
                // If not, then use the value provided by the SA "otelAttributes"
                // If none from above, then default to the "url"
                const processingUrl = !originalUrl || (originalUrl === null || originalUrl === void 0 ? void 0 : originalUrl.length) === 0
                    ? ((_a = response === null || response === void 0 ? void 0 : response.otelAttributes) === null || _a === void 0 ? void 0 : _a.originalUrl) || url
                    : originalUrl;
                span.addEvent(`${tracerName} request url: ${processingUrl}`);
                span.setAttribute('app.http_url', processingUrl);
                resolve(response);
                // Append additional attributes received from SA response
                const additionalAttributesFromSa = (_b = response === null || response === void 0 ? void 0 : response.otelAttributes) === null || _b === void 0 ? void 0 : _b.additionalAttributes;
                if (additionalAttributesFromSa &&
                    Object.keys(additionalAttributesFromSa).length > 0) {
                    span.setAttributes(additionalAttributesFromSa);
                }
                span.addEvent(`${tracerName} resolved`);
                if (response.failure) {
                    span.setStatus({ code: SpanStatusCode.ERROR });
                }
                else {
                    span.setStatus({ code: SpanStatusCode.OK });
                }
            }
            catch (error) {
                reject(error);
                span.addEvent(`${tracerName} threw error: `, error);
                span.recordException(error);
                span.setStatus({ code: SpanStatusCode.ERROR });
            }
            finally {
                span.addEvent(`${tracerName} completed`);
                span.end();
            }
        }));
    });
}
const defaultProviderOpts = {
    serviceName: 'selfservice-unknown-ui',
    namespace: 'unknown-namespace',
    version: 'unknown-version',
    language: 'javascript',
    buildEnv: 'unknown-environment',
    exporterConfig: {},
    spanConfig: {},
    handleFido: true,
    documentLoadTracking: false,
    ignoreTraceUrls: [],
    propagateUrls: []
};
function setupProvider(opts = defaultProviderOpts) {
    const { serviceName, namespace, version, language, buildEnv, exporterConfig, spanConfig, handleFido, documentLoadTracking, ignoreTraceUrls = [], propagateUrls = [] } = opts;
    const provider = new WebTracerProvider({
        resource: new Resource({
            [SemanticResourceAttributes.SERVICE_NAME]: serviceName,
            [SemanticResourceAttributes.SERVICE_NAMESPACE]: namespace,
            [SemanticResourceAttributes.SERVICE_VERSION]: version,
            [SemanticResourceAttributes.TELEMETRY_SDK_LANGUAGE]: language,
            [SemanticResourceAttributes.DEPLOYMENT_ENVIRONMENT]: buildEnv
        })
    });
    const traceExporter = new OTLPTraceExporter(exporterConfig);
    const batchExporter = new BatchSpanProcessor(traceExporter, Object.assign({ maxQueueSize: 10000, maxExportBatchSize: 20, scheduledDelayMillis: 100, exportTimeoutMillis: 30000 }, spanConfig));
    provider.addSpanProcessor(batchExporter);
    provider.register({
        contextManager: new ZoneContextManager(),
        propagator: new CompositePropagator({
            propagators: [new W3CBaggagePropagator(), new W3CTraceContextPropagator()]
        })
    });
    const urlPropagation = [
        ...propagateUrls,
        /.business.comcast.com|.business.dev.comcast.com|.business.qa.comcast.com|.business.pqa.comcast.com|.business.stg.comcast.com|.codebig2.net|.api-business.comcast.com|commonPaymentComponent/g
    ];
    const urlsToIgnore = [
        ...ignoreTraceUrls,
        /akstat.io|metrics.comcast.com|go-mpulse.net|iperceptions.com|securiti.ai|comcastcom.d1.sc.omtrdc.net|web-analytics|linkedin.com|pinterest.com/g
    ];
    const instrumentations = [
        ...(handleFido
            ? [
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls: urlPropagation,
                    ignoreUrls: urlsToIgnore
                })
            ]
            : [
                new FetchInstrumentation({
                    propagateTraceHeaderCorsUrls: urlPropagation,
                    ignoreUrls: urlsToIgnore,
                    clearTimingResources: true
                }),
                new XMLHttpRequestInstrumentation({
                    propagateTraceHeaderCorsUrls: urlPropagation,
                    ignoreUrls: urlsToIgnore
                })
            ])
    ];
    if (documentLoadTracking) {
        instrumentations.push(new DocumentLoadInstrumentation());
    }
    registerInstrumentations({
        tracerProvider: provider,
        instrumentations
    });
}

export { sendSpecificSpan, setupProvider, spanRequest };
